import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  CircularProgress,
  Chip,
  LinearProgress
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Schedule as ScheduleIcon,
  Restaurant as FoodIcon,
  LocalCafe as CafeIcon,
  Celebration as PartyIcon,
  AccessTime as TimeIcon,
  Campaign as BroadcastIcon
} from '@mui/icons-material';
import trackingService from '../../services/trackingService';

// Design System Colors (matching TrackingDashboard)
const colors = {
  primary: '#485869',
  primaryLight: '#6c7c8c',
  primaryDark: '#3A4754',
  success: '#66CC33',
  error: '#fa5b5b',
  warning: '#FF9933',
  info: '#4DBCF3',
  background: '#F5F6F7',
  paper: '#FFFFFF',
  textPrimary: '#485869',
  textSecondary: '#6B757F',
  grey50: '#F5F6F7',
  grey200: '#C8CDD2',
  grey500: '#6B757F',
  grey700: '#485869',
};

// Event type icons and colors
const eventConfig = {
  soup: { icon: '🍲', color: '#FF6B6B', label: 'Soup' },
  lunch: { icon: '🍛', color: '#4ECDC4', label: 'Lunch' },
  juice: { icon: '🍋', color: '#FFE66D', label: 'Juice' },
  snacks: { icon: '🥪', color: '#95E1D3', label: 'Snacks' }
};

// Stats Card Component
const StatCard = ({ title, value, subtitle, icon, color = colors.primary, loading = false }) => (
  <Card
    elevation={2}
    sx={{
      height: '100%',
      bgcolor: colors.paper,
      borderRadius: 2,
      transition: 'box-shadow 0.2s',
      '&:hover': { boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)' }
    }}
  >
    <CardContent sx={{ p: 3 }}>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} sx={{ color }} />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="body2" sx={{ color: colors.textSecondary, fontWeight: 500 }}>
              {title}
            </Typography>
            <Typography variant="h5" sx={{ color, fontWeight: 700, mt: 1 }}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box sx={{ color, opacity: 0.6, fontSize: 36 }}>
            {icon}
          </Box>
        </Box>
      )}
    </CardContent>
  </Card>
);

const HackathonScheduleTab = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [schedule, setSchedule] = useState([]);
  const [currentIst, setCurrentIst] = useState('');
  const [currentMessage, setCurrentMessage] = useState(null);
  const [statuslinePreview, setStatuslinePreview] = useState(null);

  // Fetch schedule data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [scheduleData, broadcastData, statuslineData] = await Promise.all([
        trackingService.getHackathonSchedule(),
        trackingService.getHackathonBroadcast(),
        trackingService.getStatuslineMessage()
      ]);

      setSchedule(scheduleData.schedule || []);
      setCurrentIst(scheduleData.current_ist || '');
      setCurrentMessage(broadcastData);
      setStatuslinePreview(statuslineData);
    } catch (err) {
      console.error('Failed to fetch hackathon schedule:', err);
      setError(err.message || 'Failed to load hackathon schedule');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    // Refresh every minute to keep time current
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Group schedule by date
  const scheduleByDate = schedule.reduce((acc, item) => {
    if (!acc[item.date]) {
      acc[item.date] = [];
    }
    acc[item.date].push(item);
    return acc;
  }, {});

  // Format date for display
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Check if an event is happening now or soon
  const getEventStatus = (date, time) => {
    if (!currentIst) return 'upcoming';

    const [currentDate, currentTime] = currentIst.split(' ');
    if (date < currentDate) return 'past';
    if (date > currentDate) return 'upcoming';

    // Same date - compare times
    const eventMinutes = parseInt(time.split(':')[0]) * 60 + parseInt(time.split(':')[1]);
    const currentMinutes = parseInt(currentTime.split(':')[0]) * 60 + parseInt(currentTime.split(':')[1]);

    if (currentMinutes > eventMinutes + 60) return 'past';
    if (currentMinutes >= eventMinutes && currentMinutes <= eventMinutes + 60) return 'active';
    if (currentMinutes >= eventMinutes - 30) return 'soon';
    return 'upcoming';
  };

  if (loading && schedule.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress sx={{ color: colors.primary }} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ color: colors.textPrimary, fontWeight: 700 }}>
            Hackathon Schedule
          </Typography>
          <Typography variant="body2" sx={{ color: colors.textSecondary }}>
            Dec 20-21, 2025 | All times in IST (Indian Standard Time)
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchData}
          disabled={loading}
          sx={{
            borderColor: colors.primary,
            color: colors.primary,
            textTransform: 'none',
            '&:hover': { borderColor: colors.primaryDark, bgcolor: colors.grey50 }
          }}
        >
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Current IST Time"
            value={currentIst || '--:--'}
            icon={<TimeIcon />}
            color={colors.info}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Events"
            value={schedule.length}
            subtitle="Across 2 days"
            icon={<ScheduleIcon />}
            color={colors.primary}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Hackathon Status"
            value="Active"
            subtitle="Dec 20-21"
            icon={<PartyIcon />}
            color={colors.success}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Locations"
            value="2"
            subtitle="Juice Joint & Tiffany"
            icon={<FoodIcon />}
            color={colors.warning}
            loading={loading}
          />
        </Grid>
      </Grid>

      {/* Current Broadcast Message */}
      {currentMessage && currentMessage.has_message && (
        <Card elevation={2} sx={{ mb: 4, borderRadius: 2, border: `2px solid ${colors.success}` }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <BroadcastIcon sx={{ color: colors.success }} />
              <Typography variant="h6" sx={{ color: colors.textPrimary, fontWeight: 600 }}>
                Current Status Line Message
              </Typography>
            </Box>
            <Paper
              sx={{
                p: 2,
                bgcolor: '#1a1a1a',
                borderRadius: 1,
                fontFamily: 'monospace'
              }}
            >
              <Typography sx={{ color: '#00ff00', fontSize: '1rem' }}>
                {currentMessage.text}
              </Typography>
            </Paper>
            <Typography variant="caption" sx={{ color: colors.textSecondary, mt: 1, display: 'block' }}>
              This message is currently showing in Claude Code status line for all users
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Status Line Preview */}
      {statuslinePreview && statuslinePreview.has_message && (
        <Card elevation={2} sx={{ mb: 4, borderRadius: 2 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="subtitle2" sx={{ color: colors.textSecondary, mb: 2, fontWeight: 600 }}>
              STATUS LINE PREVIEW (what users see):
            </Typography>
            <Paper
              sx={{
                p: 2,
                bgcolor: '#2d2d2d',
                borderRadius: 1,
                fontFamily: 'monospace',
                overflow: 'auto'
              }}
            >
              <Typography sx={{ color: '#e0e0e0', fontSize: '0.9rem', whiteSpace: 'nowrap' }}>
                {statuslinePreview.text} | Claude Opus 4 | 50k/200k (25%) | $0.15 | 45m
              </Typography>
            </Paper>
          </CardContent>
        </Card>
      )}

      {/* Schedule Tables by Date */}
      {Object.entries(scheduleByDate).map(([date, events]) => (
        <Card key={date} elevation={2} sx={{ mb: 3, borderRadius: 2 }}>
          <CardContent sx={{ p: 0 }}>
            <Box sx={{ p: 2, bgcolor: colors.grey50, borderBottom: `1px solid ${colors.grey200}` }}>
              <Typography variant="h6" sx={{ color: colors.textPrimary, fontWeight: 700 }}>
                {formatDate(date)}
              </Typography>
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: colors.grey50 }}>
                    <TableCell sx={{ fontWeight: 600, color: colors.textSecondary, width: 100 }}>Time</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: colors.textSecondary, width: 120 }}>Event</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: colors.textSecondary }}>Item</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: colors.textSecondary }}>Location</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: colors.textSecondary, width: 120 }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {events.map((event, idx) => {
                    const config = eventConfig[event.event] || { icon: '📌', color: colors.primary, label: event.event };
                    const status = getEventStatus(date, event.time);

                    return (
                      <TableRow
                        key={idx}
                        sx={{
                          bgcolor: status === 'active' ? 'rgba(102, 204, 51, 0.1)' :
                                   status === 'soon' ? 'rgba(255, 153, 51, 0.1)' :
                                   'transparent',
                          '&:hover': { bgcolor: colors.grey50 }
                        }}
                      >
                        <TableCell>
                          <Typography variant="body1" sx={{ fontWeight: 600, color: colors.textPrimary }}>
                            {event.time}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography fontSize="1.5rem">{config.icon}</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: config.color }}>
                              {config.label}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ color: colors.textPrimary }}>
                            {event.item}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={event.location}
                            size="small"
                            sx={{
                              bgcolor: event.location === 'Tiffany' ? colors.info : colors.warning,
                              color: 'white',
                              fontWeight: 500
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={
                              status === 'active' ? 'NOW' :
                              status === 'soon' ? 'Soon (30m)' :
                              status === 'past' ? 'Done' : 'Upcoming'
                            }
                            size="small"
                            sx={{
                              bgcolor:
                                status === 'active' ? colors.success :
                                status === 'soon' ? colors.warning :
                                status === 'past' ? colors.grey500 : colors.info,
                              color: 'white',
                              fontWeight: 600,
                              animation: status === 'active' ? 'pulse 2s infinite' : 'none',
                              '@keyframes pulse': {
                                '0%': { opacity: 1 },
                                '50%': { opacity: 0.6 },
                                '100%': { opacity: 1 }
                              }
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      ))}

      {/* Message Types Legend */}
      <Card elevation={1} sx={{ borderRadius: 2, bgcolor: colors.grey50 }}>
        <CardContent sx={{ p: 2 }}>
          <Typography variant="subtitle2" sx={{ color: colors.textSecondary, mb: 2, fontWeight: 600 }}>
            BROADCAST MESSAGE TYPES:
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography fontSize="1.2rem">🍲</Typography>
                <Typography variant="body2" sx={{ color: colors.textPrimary }}>
                  <strong>Soup:</strong> Warm up your brain!
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography fontSize="1.2rem">🍛</Typography>
                <Typography variant="body2" sx={{ color: colors.textPrimary }}>
                  <strong>Lunch:</strong> Save work, feed your soul!
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography fontSize="1.2rem">🍋</Typography>
                <Typography variant="body2" sx={{ color: colors.textPrimary }}>
                  <strong>Juice:</strong> Stay hydrated!
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography fontSize="1.2rem">🥪</Typography>
                <Typography variant="body2" sx={{ color: colors.textPrimary }}>
                  <strong>Snacks:</strong> Quick bite, big ideas!
                </Typography>
              </Box>
            </Grid>
          </Grid>
          <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${colors.grey200}` }}>
            <Typography variant="caption" sx={{ color: colors.textSecondary }}>
              Messages automatically appear 30 minutes before each event, during the event, and as motivational reminders in between.
              Late night (12AM-6AM) and early morning (6AM-9AM) have special messages too!
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default HackathonScheduleTab;
